"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight } from "lucide-react";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { hasPermission } from "@/features/admin/utils";
import { t } from "@/lib/localization";
import {
    NavigationGroup,
    NavigationItem,
    NavigationLeaf,
    NavigationSection,
} from "./sidebar";
import { User } from "next-auth";

const isGroup = (item: NavigationItem): item is NavigationGroup =>
    "children" in item;

function isItemVisible(item: NavigationItem, user: User): boolean {
    if (isGroup(item)) {
        if (item.adminOnly && !user.admin) return false;
        return item.children.some(
            (child) =>
                !child.requiredPermission ||
                hasPermission(user, child.requiredPermission)
        );
    }
    return (
        !item.requiredPermission || hasPermission(user, item.requiredPermission)
    );
}

export default function SidebarNavSection({
    section,
}: {
    section: NavigationSection;
}) {
    const { user } = useAuth();

    if (!user) {
        return null;
    }

    const items = section.items.filter((item) => isItemVisible(item, user));

    if (items.length === 0) {
        return null;
    }

    return (
        <SidebarGroup>
            <SidebarGroupLabel>{t(section.keyName)}</SidebarGroupLabel>
            <SidebarMenu>
                {items.map((item) =>
                    isGroup(item) ? (
                        <CollapsibleNavItem
                            key={item.keyName}
                            item={item}
                            user={user}
                        />
                    ) : (
                        <LeafNavItem key={item.keyName} item={item} />
                    )
                )}
            </SidebarMenu>
        </SidebarGroup>
    );
}

function LeafNavItem({ item }: { item: NavigationLeaf }) {
    const pathname = usePathname();
    const label = t(item.keyName);

    if (item.comingSoon) {
        return (
            <SidebarMenuItem>
                <SidebarMenuButton
                    tooltip={label}
                    className="pointer-events-none font-medium"
                >
                    <item.icon />
                    <span className="truncate text-muted-foreground">
                        {label}
                    </span>
                    <Badge
                        variant="outline"
                        className="ms-auto shrink-0 px-1.5 py-0 text-[10px] font-normal leading-tight"
                    >
                        {t("platform.sidebar.coming-soon-badge")}
                    </Badge>
                </SidebarMenuButton>
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                asChild
                isActive={pathname.includes(item.href)}
                tooltip={label}
                className="font-medium"
            >
                <Link href={item.href}>
                    <item.icon />
                    <span>{label}</span>
                </Link>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}

function CollapsibleNavItem({
    item,
    user,
}: {
    item: NavigationGroup;
    user: User;
}) {
    const pathname = usePathname();
    const label = t(item.keyName);

    const children = item.children.filter(
        (child) =>
            !child.requiredPermission ||
            hasPermission(user, child.requiredPermission)
    );

    const hasActiveChild = children.some((child) =>
        pathname.includes(child.href)
    );

    return (
        <Collapsible
            asChild
            defaultOpen={hasActiveChild}
            className="group/collapsible"
        >
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={label} className="font-medium">
                        <item.icon />
                        <span>{label}</span>
                        <ChevronRight className="ms-auto size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:rotate-180 rtl:group-data-[state=open]/collapsible:!rotate-90 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        {children.map((child) => (
                            <SidebarMenuSubItem key={child.keyName}>
                                <SidebarMenuSubButton
                                    asChild
                                    isActive={pathname.includes(child.href)}
                                    className="text-muted-foreground hover:text-muted-foreground data-[active=true]:text-muted-foreground"
                                >
                                    <Link href={child.href}>
                                        <span>{t(child.keyName)}</span>
                                    </Link>
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}
